/** A record panel's field grid whose "needs a choice" questions (plan §1.3) span the row
 *  under their field's row, so a question's answers read on one line each instead of
 *  wrapping inside one field column.
 *
 *  A question's card follows the LAST cell of the grid row its field sits in — measured
 *  from the grid's resolved column count, placed the way CSS auto-placement places the
 *  cells (a wide cell takes a row of its own, a spanning cell moves to the next row when
 *  the rest of its row is too narrow) — so DOM order is reading order and no field leaves
 *  its row. Unmeasured (jsdom), a card follows its field's cell.
 *
 *  A cell may span columns (`span`, a picker whose identity line needs the room) — never
 *  more than the grid has: one column, it takes one.
 *
 *  The cells render their FieldSlot with `inlineChoices={false}`. A list widget (the
 *  contacts) places its questions under the item that raised them itself, so its cell
 *  gets no question row.
 *
 *      <FieldCellGrid sx={FIELD_GRID_SX} cells={[{ key, rec, path, wide, node: <FieldSlot … /> }]} />
 */
import { Box, type SxProps, type Theme } from '@mui/material';
import { Fragment, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { parseRecordKey, type RecordKey } from '../draft/changeSet';
import { anchorKey } from '../editor/choices';
import { useRecordEditor } from '../editor/RecordEditorProvider';

import { ChoiceQuestion } from './ChoiceQuestion';

export interface FieldCell {
  key: string;
  rec: RecordKey;
  /** The field's draft path — what its questions anchor to. */
  path: string;
  /** Takes the whole row (`gridColumn: 1 / -1`). */
  wide: boolean;
  /** Columns the cell spans, at most the grid's (default 1). */
  span?: number;
  node: ReactNode;
}

/** The resolved column count of a grid ("243px 243px 243px" → 3); 0 when unresolved. */
function resolvedColumns(el: HTMLElement): number {
  const tracks = getComputedStyle(el).gridTemplateColumns.trim();
  return /^(\d+(\.\d+)?px\s*)+$/.test(tracks) ? tracks.split(/\s+/).length : 0;
}

/** For each cell, the index of the last cell of its grid row — auto-placement without
 *  dense packing: a wide cell (span `columns`) closes the row before it and fills one of
 *  its own; a cell that does not fit the rest of its row starts the next. */
export function rowEnds(spans: number[], columns: number): number[] {
  const ends: number[] = new Array<number>(spans.length);
  let row: number[] = [];
  let used = 0;
  const close = () => {
    for (const i of row) ends[i] = row[row.length - 1]!;
    row = [];
    used = 0;
  };
  spans.forEach((span, i) => {
    const s = Math.min(Math.max(1, span), columns);
    if (used + s > columns) close();
    row.push(i);
    used += s;
    if (used === columns) close();
  });
  close();
  return ends;
}

export function FieldCellGrid({ sx, cells }: { sx: SxProps<Theme>; cells: FieldCell[] }) {
  const { mode, raised, fieldOf, widgetOf } = useRecordEditor();
  const ref = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setColumns(resolvedColumns(el));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const asked = mode === 'edit' ? raised.filter((c) => c.anchor !== null) : [];
  const ownChoices = (c: FieldCell) => {
    const field = fieldOf(parseRecordKey(c.rec).entity, c.path);
    return field !== undefined && widgetOf(field)?.owns === 'items';
  };
  const choicesOf = (c: FieldCell) =>
    ownChoices(c) ? [] : asked.filter((q) => q.anchor === anchorKey(c.rec, c.path));
  const spanOf = (c: FieldCell) => (c.wide ? columns : Math.min(c.span ?? 1, Math.max(1, columns)));
  const ends = columns > 0 ? rowEnds(cells.map(spanOf), columns) : cells.map((_, i) => i);
  // The cards to render after each cell: those of every field whose row ends there.
  const after = new Map<number, FieldCell[]>();
  cells.forEach((c, i) => {
    if (choicesOf(c).length === 0) return;
    const end = ends[i] ?? i;
    after.set(end, [...(after.get(end) ?? []), c]);
  });

  return (
    <Box ref={ref} sx={sx}>
      {cells.map((c, i) => (
        <Fragment key={c.key}>
          <Box sx={c.wide ? { gridColumn: '1 / -1' } : spanOf(c) > 1 ? { gridColumn: `span ${spanOf(c)}` } : undefined}>
            {c.node}
          </Box>
          {after.get(i)?.map((owner) => (
            <Box
              key={`choices:${owner.key}`}
              data-field-choices={anchorKey(owner.rec, owner.path)}
              sx={{ gridColumn: '1 / -1', display: 'grid', gap: 0.5, mb: 1 }}
            >
              {choicesOf(owner).map((q) => (
                <ChoiceQuestion key={q.option.key} choice={q} />
              ))}
            </Box>
          ))}
        </Fragment>
      ))}
    </Box>
  );
}
